import React, { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { renderWithLatex } from "../utils/latexRenderer";
import RequireAuth from "../components/RequireAuth";

const API_BASE = "https://eluvent.com/api";

const examDetails = {
  navodaya_class6: { title: "Navodaya Class 6", total_marks: 100, time_minutes: 120, description: "80 questions", exam_type: "navodaya-class-6" },
  navodaya_class9: { title: "Navodaya Class 9", total_marks: 100, time_minutes: 150, description: "100 questions", exam_type: "navodaya-class-9" },
  nstse: { title: "NSTSE", total_marks: 60, time_minutes: 60, description: "60 questions", exam_type: "nstse" },
};

const CompetitiveExamRunnerContent = () => {
  const { examType } = useParams();
  const [searchParams] = useSearchParams();
  const classLevel = searchParams.get("class") || "";
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paper, setPaper] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentSection, setCurrentSection] = useState(0);
  const [timer, setTimer] = useState(0);
  const timerRef = useRef(null);
  const [screen, setScreen] = useState("confirm");
  const [report, setReport] = useState(null);
  const [status, setStatus] = useState("");

  const details = examDetails[examType] || { exam_type: examType, title: examType, time_minutes: 60, description: "" };

  useEffect(() => {
    axios.get(`${API_BASE}/auth/user`, { withCredentials: true })
      .then(res => { if (res.data && res.data.id) setUser(res.data); })
      .catch(() => {})
      .finally(() => setAuthLoading(false));
  }, []);

  useEffect(() => {
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const formatOptions = (q) => {
    let optsObj = {};
    if (Array.isArray(q.options)) {
      const letters = ["A", "B", "C", "D", "E"];
      q.options.forEach((opt, i) => { optsObj[letters[i]] = opt; });
    } else if (typeof q.options === "object" && q.options !== null) {
      optsObj = q.options;
    }
    let correctLetter = "A";
    if (typeof q.correct === "number") {
      correctLetter = ["A", "B", "C", "D", "E"][q.correct] || "A";
    } else if (typeof q.correct === "string") {
      correctLetter = q.correct.toUpperCase();
    }
    return { question: q.question, options: optsObj, correct_answer: correctLetter, topic: q.topic || "General" };
  };

  const startExam = async () => {
    setLoading(true);
    setError(null);
    setStatus("Preparing your paper... (may take 30-60 seconds)");
    try {
      const res = await axios.post(`${API_BASE}/competitive/generate`, { exam_type: details.exam_type || examType }, { withCredentials: true });
      const data = res.data;
      let sections = [];
      if (data.sections && Array.isArray(data.sections) && data.sections.length > 0) {
        sections = data.sections.map((s) => ({
          title: s.name || s.title || "Section",
          marks_each: s.marks_each || 1,
          questions: (s.questions || []).map(formatOptions)
        }));
      } else if (data.questions && Array.isArray(data.questions) && data.questions.length > 0) {
        sections = [{ title: "Section A", marks_each: 1, questions: data.questions.map(formatOptions) }];
      } else {
        setError("No questions were generated. Please try again.");
        setLoading(false);
        setStatus("");
        return;
      }
      const totalQuestions = sections.reduce((sum, s) => sum + s.questions.length, 0);
      if (totalQuestions === 0) {
        setError("No questions were generated. Please try again.");
        setLoading(false);
        setStatus("");
        return;
      }
      const newPaper = {
        title: data.exam_name || details.title,
        total_marks: data.total_marks || totalQuestions,
        time_minutes: data.duration_minutes || details.time_minutes || 60,
        sections: sections
      };
      setPaper(newPaper);
      setTimer(newPaper.time_minutes * 60);
      setScreen("exam");
      setStatus("");
      setLoading(false);
      setCurrentSection(0);
      setAnswers({});
      timerRef.current = setInterval(() => {
        setTimer(prev => {
          if (prev <= 1) { clearInterval(timerRef.current); return 0; }
          return prev - 1;
        });
      }, 1000);
    } catch (e) {
      console.error("Generation error:", e);
      setError("Failed to generate paper. Please try again.");
      setLoading(false);
      setStatus("");
    }
  };

  const selectAnswer = (si, qi, opt) => setAnswers(prev => ({ ...prev, [`${si}-${qi}`]: opt }));
  const formatTime = (s) => `${Math.floor(s / 60)}:${s % 60 < 10 ? "0" : ""}${s % 60}`;

  const handleSubmit = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    let correct = 0;
    let total = 0;
    const sectionScores = [];
    paper.sections.forEach((sec, si) => {
      let secCorrect = 0;
      let secTotal = 0;
      sec.questions.forEach((q, qi) => {
        secTotal += 1;
        total += 1;
        const userAns = answers[`${si}-${qi}`];
        if (userAns && userAns === q.correct_answer) { correct += 1; secCorrect += 1; }
      });
      sectionScores.push({ title: sec.title, correct: secCorrect, total: secTotal });
    });
    const percentage = total > 0 ? Math.round((correct / total) * 100) : 0;
    setReport({ score: correct, total_marks: total, percentage, sectionScores });
    setScreen("report");
  };

  if (authLoading) return <div style={{ textAlign: "center", padding: 60 }}>Loading...</div>;

  if (!user) {
    const url = window.location.pathname + window.location.search;
    return (
      <div style={{ textAlign: "center", marginTop: 60, padding: 20 }}>
        <h1>🔐 {details.title}</h1>
        <p>{details.description}</p>
        <p>Total Marks: {details.total_marks} | Duration: {details.time_minutes} min</p>
        <p style={{ color: "#666", marginTop: 20 }}>Please log in with Google to continue.</p>
        <a href={`https://eluvent.com/auth/google/login?next=${encodeURIComponent(url)}`} style={{ display: "inline-block", marginTop: 20, padding: "14px 28px", fontSize: 18, background: "#fca311", color: "white", border: "none", borderRadius: 30, fontWeight: 700, cursor: "pointer", textDecoration: "none" }}>
          Login with Google
        </a>
      </div>
    );
  }

  if (screen === "confirm") {
    return (
      <div style={{ textAlign: "center", marginTop: 60, padding: 20 }}>
        <h1>{details.title}</h1>
        <p>{details.description}</p>
        <p>Total Marks: {details.total_marks} | Duration: {details.time_minutes} min</p>
        {status && <p style={{ color: "#7c3aed" }}>{status}</p>}
        {error && <p style={{ color: "red" }}>{error}</p>}
        <button onClick={startExam} disabled={loading} style={{ padding: "14px 28px", fontSize: 18, background: "#fca311", color: "white", border: "none", borderRadius: 30, fontWeight: 700, cursor: "pointer", marginTop: 20 }}>
          {loading ? "Preparing..." : "Start Mock Test"}
        </button>
      </div>
    );
  }

  if (screen === "exam" && paper) {
    const sec = paper.sections[currentSection];
    return (
      <div style={{ maxWidth: 900, margin: "0 auto", padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2>{paper.title}</h2>
          <h3 style={{ color: timer < 60 ? "red" : "#333" }}>⏱ {formatTime(timer)}</h3>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
          {paper.sections.map((s, i) => (
            <button key={i} onClick={() => setCurrentSection(i)} style={{ padding: "8px 16px", borderRadius: 20, border: currentSection === i ? "2px solid #7c3aed" : "1px solid #ccc", background: currentSection === i ? "#ede9fe" : "white", cursor: "pointer", fontSize: 13, fontWeight: currentSection === i ? 700 : 400 }}>
              {s.title} ({s.questions.length})
            </button>
          ))}
        </div>
        <div style={{ background: "#ede9fe", padding: 12, borderRadius: 10, marginBottom: 16 }}>
          <strong>{sec.title}</strong> — {sec.questions.length} questions
        </div>
        {sec.questions.map((q, qi) => {
          const k = `${currentSection}-${qi}`;
          const a = answers[k] || null;
          return (
            <div key={qi} style={{ background: "white", padding: 16, marginBottom: 12, borderRadius: 12 }}>
              <p><strong>Q{qi + 1}.</strong> {renderWithLatex(q.question)}</p>
              {Object.entries(q.options || {}).map(([opt, text]) => (
                <label key={opt} style={{ display: "block", margin: "5px 0", cursor: "pointer" }}>
                  <input type="radio" name={k} value={opt} checked={a === opt} onChange={() => selectAnswer(currentSection, qi, opt)} />{" "}
                  {opt.toUpperCase()}. {renderWithLatex(text)}
                </label>
              ))}
            </div>
          );
        })}
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
          <button disabled={currentSection === 0} onClick={() => setCurrentSection(Math.max(0, currentSection - 1))} style={{ padding: "10px 20px", cursor: currentSection === 0 ? "not-allowed" : "pointer", borderRadius: 8 }}>← Previous</button>
          {currentSection < paper.sections.length - 1 ? (
            <button onClick={() => setCurrentSection(currentSection + 1)} style={{ padding: "10px 20px", cursor: "pointer", background: "#7c3aed", color: "white", border: "none", borderRadius: 8 }}>Next Section →</button>
          ) : (
            <button onClick={handleSubmit} style={{ padding: "10px 20px", cursor: "pointer", background: "#10b981", color: "white", border: "none", borderRadius: 8, fontWeight: 700 }}>Submit Exam</button>
          )}
        </div>
      </div>
    );
  }

  if (screen === "report" && report) {
    return (
      <div style={{ maxWidth: 700, margin: "40px auto", padding: 20, textAlign: "center" }}>
        <h1>Exam Report</h1>
        <div style={{ background: "#f5f3ff", padding: 24, borderRadius: 16, marginBottom: 20 }}>
          <h2 style={{ color: "#7c3aed" }}>{report.percentage}%</h2>
          <p>Score: {report.score} / {report.total_marks}</p>
        </div>
        <h3>Section Breakdown</h3>
        {report.sectionScores && report.sectionScores.map((s, i) => (
          <div key={i} style={{ background: "white", padding: 12, marginBottom: 8, borderRadius: 8, textAlign: "left" }}>
            <strong>{s.title}</strong>: {s.correct}/{s.total} ({Math.round((s.correct/s.total)*100)}%)
          </div>
        ))}
        <button onClick={() => { setScreen("confirm"); setPaper(null); setAnswers({}); setReport(null); }} style={{ marginTop: 20, padding: "12px 24px", background: "#7c3aed", color: "white", border: "none", borderRadius: 25, fontWeight: 700, cursor: "pointer" }}>
          Take Another Test
        </button>
      </div>
    );
  }

  return null;
};

const CompetitiveExamRunner = () => (
  <RequireAuth>
    <CompetitiveExamRunnerContent />
  </RequireAuth>
);

export default CompetitiveExamRunner;
